import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Header from 'components/header/Header.container';
import InputField from 'components/textField/InputField';
import searchIcon from 'assets/svg/search.svg';
import locationInModalIcon from 'assets/svg/location-in-modal.svg';
import peopleGroupInModalIcon from 'assets/svg/people-group-in-modal.svg';
import warningCircleIcon from 'assets/svg/warning-circle.svg';
import rechargeAccountIcon from 'assets/svg/recharge-account.svg';
import browseRoomsIcon from 'assets/svg/browser-rooms.svg';
import logoImage from 'assets/svg/logo.svg';
import joinGroupIcon from 'assets/svg/join-group.svg';
import chatOutlineIcon from 'assets/svg/chat_outline.svg';
import startYourOwnRoomIcon from 'assets/svg/start-your-own-room.svg';
import superHostJoinIcon from 'assets/svg/superhostroom-join-modal.svg';
import closeModalIcon from 'assets/svg/close-icon-in-modal.svg';
import superHostKey from 'assets/svg/superhost-user-key-indicator-for-avatar.svg';
import NearbyItem from 'views/conversationsNearby/nearbyItem/index';
import CompactMobileSearch from 'components/compactMobileSearch/CompactMobileSearch';
import { useCompactMobileSearch } from 'hooks/useCompactMobileSearch';
import SliderWrapper from 'components/controls/Slider/SliderWrapper';
import { broadCastNearby } from 'components/controls/Slider/SliderResponsiveness';
import CustomModal from 'components/customModal/CustomModal';
import Button from 'components/button/Button';
import Footer from 'components/footer/Footer';
import Styles from './ConversationsNearby.module.scss';
import ProfileStyles from 'views/profile/publicProfile/PublicProfile.module.scss';
import ProfileAvatar from 'components/profileAvatar/ProfileAvatar';
import { conversation } from 'helpers/data/conversations';
import { PHONE_MAX_WIDTH } from 'helpers/viewportBreakpoints';
import { getDistance, normalizeDistance, getCurrentDate, randomString, getParticipantsArray, getParticipantsCount } from 'helpers/utils';
import { useGetCallStatus, useGetRooms, sendJoinRequest } from 'store/services/firebase-broadcast';
import { toast } from 'react-toastify';
import { trackSearch, trackModalInteraction, trackNavigation } from 'utils/clarity';
import CardItem from '../profile/publicProfile/card/item';
import distanceIcon from 'assets/svg/distance.svg';
import userIcon from 'assets/svg/topic-user.svg';
import groupChatIcon from 'assets/svg/group-chat-icon.svg';

interface ConversationsNearbyProps {
  nearbyRooms: Function;
  joinRequest: Function;
  getRoom: Function;
  createLocation: Function;
  rooms: any;
  createdRoom: any;
  allNearByRooms: any;
  loader: boolean;
  getPublicProfile: Function;
  userPublicProfile: any;
  followProfile: Function;
}

const SKELETON_COUNT = 6;
const MOBILE_LAYOUT_QUERY = `(max-width: ${PHONE_MAX_WIDTH}px)`;

const SkeletonCard: React.FC = () => (
  <div className={Styles.skeletonCard}>
    <div className={Styles.skeletonHeader} />
    <div className={Styles.skeletonAvatar} />
    <div className={Styles.skeletonLine} />
    <div className={Styles.skeletonLineSm} />
    <div className={Styles.skeletonDivider} />
    <div className={Styles.skeletonParticipant} />
    <div className={Styles.skeletonParticipant} />
    <div className={Styles.skeletonParticipant} />
    <div className={Styles.skeletonDivider} />
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div className={Styles.skeletonBtn} />
      <div className={Styles.skeletonBtn} />
    </div>
  </div>
);

const MobileSkeletonCard: React.FC = () => (
  <div className={Styles.skeletonCardMobile}>
    <div className={Styles.skeletonHeader} />
    <div className={Styles.skeletonMobileBody}>
      <div className={Styles.skeletonMobileHost}>
        <div className={Styles.skeletonAvatar} />
        <div className={Styles.skeletonLine} />
        <div className={Styles.skeletonLineSm} />
      </div>
      <div className={Styles.skeletonMobileDivider} />
      <div className={Styles.skeletonMobileSummary}>
        <div className={Styles.skeletonLine} />
      </div>
    </div>
    <div className={Styles.skeletonMobileActions}>
      <div className={Styles.skeletonBtn} />
      <div className={Styles.skeletonBtn} />
    </div>
  </div>
);

const ConversationsNearby: FunctionComponent<ConversationsNearbyProps> = (props) => {
  const { createLocation, nearbyRooms, allNearByRooms, loader, getPublicProfile, userPublicProfile, followProfile } = props;
  const user = JSON.parse(localStorage.getItem('user') as string);
  const history = useHistory();

  // ── Modal state ──────────────────────────────────────────────────────────
  const [isExpand, setIsExpand]           = useState(false);
  const [isJoinRoom, setIsJoinRoom]       = useState(false);
  const [isNoGoldPiece, setIsNoGoldPiece] = useState(false);
  const [isNoFees, setIsNoFees]           = useState(false);
  const [createRoomPopup, setCreateRoomPopup] = useState(false);
  const [modalToggle, setModalToggle]     = useState(false);
  const [viewProfile, setViewProfile]     = useState(false);

  // ── Room / search state ───────────────────────────────────────────────────
  const [joinedRoom, setJoinRoom]       = useState<any>('');
  const [joinIntent, setJoinIntent]     = useState<'video' | 'chat'>('video');
  const [searchTopic, setSearchTopic]   = useState('');
  const [baseRooms, setBaseRooms]       = useState<any[]>([]);  // sorted API rooms (source of truth)
  const [expandedRoom, setExpandedRoom] = useState(conversation);
  const [joined, setJoined]             = useState(false);
  const [callStatus, setCallStatus]     = useState(false);

  // ── Profile state ─────────────────────────────────────────────────────────
  const [profile, setProfile]           = useState(user);
  const [profileRoomPk, setProfileRoomPk] = useState<number | string | null>(null);
  const [publicProfile, setPublicProfile] = useState<any>();
  const [showToolTip, setShowToolTip]   = useState(false);
  const [followToggle, setFollowToggle] = useState(false);

  // ── Location ─────────────────────────────────────────────────────────────
  const [location, setLocation] = useState({ latitude: '', longitude: '' });
  const locationFetched = useRef(false);

  // ── Live Firebase rooms (single subscription) ─────────────────────────────
  const firebaseRooms = useGetRooms();
  const status = useGetCallStatus(expandedRoom.pk);
  const token = localStorage.getItem('accessToken');

  const [isMobileLayout, setIsMobileLayout] = useState(
    () => window.matchMedia(MOBILE_LAYOUT_QUERY).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const updateLayout = () => setIsMobileLayout(mediaQuery.matches);
    updateLayout();
    mediaQuery.addEventListener('change', updateLayout);
    return () => mediaQuery.removeEventListener('change', updateLayout);
  }, []);

  const isSearchActive = searchTopic.trim().length > 0;

  const {
    searchInputRef,
    showCompactSearch,
    isSearchExpanded,
    openMobileSearch,
    handleMobileSearchBlur,
  } = useCompactMobileSearch({ isMobile: isMobileLayout, isSearchActive });

  // ── Derived filtered rooms (no extra state, no stale closures) ────────────
  const conversationNearby = useMemo(() => {
    if (!searchTopic.trim()) return baseRooms;
    const q = searchTopic.toLowerCase();
    return baseRooms.filter((room: any) =>
      room.topic?.toLowerCase().includes(q) ||
      room.host?.name?.toLowerCase().includes(q)
    );
  }, [baseRooms, searchTopic]);

  const formatLoggedHours = (minutes: any) =>
    `${((minutes || 0) / 60).toFixed(2)}hrs`;

  const profileCardDetails = useMemo(() => [
    {
      title: 'Number of Rooms Started',
      description: '',
      total: publicProfile ? publicProfile.rooms_started : '',
    },
    {
      title: 'Hours in This Room',
      description: 'How long this room has been active',
      total: publicProfile ? formatLoggedHours(publicProfile.room_logged_hours) : '',
      breakdown: publicProfile
        ? [
            { label: 'Chat', value: formatLoggedHours(publicProfile.room_logged_hours_chat) },
            { label: 'Video', value: formatLoggedHours(publicProfile.room_logged_hours_video) },
          ]
        : undefined,
    },
    {
      title: 'My Time in This Room',
      description: '',
      total: publicProfile ? formatLoggedHours(publicProfile.my_room_logged_hours) : '',
      breakdown: publicProfile
        ? [
            { label: 'Chat', value: formatLoggedHours(publicProfile.my_room_logged_hours_chat) },
            { label: 'Video', value: formatLoggedHours(publicProfile.my_room_logged_hours_video) },
          ]
        : undefined,
    },
  ], [publicProfile]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const closeModal  = useCallback(() => setModalToggle(false), []);

  const closeDialog = useCallback(() => {
    document.body.classList.remove('modal-open');
    setIsExpand(false);
    setIsJoinRoom(false);
    setIsNoGoldPiece(false);
    setIsNoFees(false);
    setViewProfile(false);
    setProfileRoomPk(null);
  }, []);

  const openProfileForRoom = useCallback((person: any, roomPk: number | string) => {
    setProfile(person);
    setProfileRoomPk(roomPk);
    setViewProfile(true);
  }, []);

  // Room the user opened the profile from (not the host's latest room from the API).
  const selectedProfileRoom = useMemo(() => {
    if (profileRoomPk == null) return null;
    const pk = Number(profileRoomPk);
    if (Number(expandedRoom?.pk) === pk) return expandedRoom;

    const fromList = baseRooms.find((room: any) => Number(room.pk) === pk);
    if (!fromList) return null;

    const live = Array.isArray(firebaseRooms)
      ? firebaseRooms.find((room: any) => Number(room.pk) === pk)
      : null;
    return live ? { ...fromList, ...live } : fromList;
  }, [profileRoomPk, expandedRoom, baseRooms, firebaseRooms]);

  const handleFollow = useCallback(() => {
    setFollowToggle(t => !t);
    followProfile({ pk: profile.pk });
  }, [followProfile, profile.pk]);

  const sendChatJoinRequest = useCallback((room: any) => {
    const request = {
      roomId: room.pk,
      id: `${room.pk}-${randomString()}`,
      user,
      time: getCurrentDate(),
      timestamp: Date.now(),
      roomTopic: room.topic,
    };

    return sendJoinRequest(request)
      .then(() => {
        toast.success('Join request has been sent');
        history.push({ pathname: '/chat-room', state: { selected: room, preserveSelection: true } });
      })
      .catch((error: { code?: string }) => {
        if (error.code === 'INVALID_USER_DATA') {
          return;
        }
        if (error.code === 'DUPLICATE_REQUEST') {
          toast.warning('You already have a pending request for this room. Please wait for the host to respond.');
          return;
        }
        toast.error('Something went wrong!');
      });
  }, [history, user]);

  const joinRoom = useCallback(() => {
    setIsExpand(false);
    document.body.classList.add('modal-open');
    if (expandedRoom.host.is_super_host) {
      joined
        ? history.push({ pathname: '/call', state: { selectedBroadCast: expandedRoom } })
        : setIsNoFees(true);
    } else {
      if (joined) {
        history.push({ pathname: '/call', state: { selectedBroadCast: expandedRoom } });
      } else if (expandedRoom.participants.length === 3) {
        setIsJoinRoom(true);
      } else {
        history.push({ pathname: '/call', state: { selectedBroadCast: expandedRoom } });
      }
    }
  }, [expandedRoom, history, joined]);

  const joinGoldPiece = useCallback(() => {
    const isRoomHost = joinedRoom?.host?.pk === user?.pk;

    if (isRoomHost) {
      setIsJoinRoom(false);
      document.body.classList.remove('modal-open');
      if (joinIntent === 'chat') {
        sendChatJoinRequest(joinedRoom);
      } else {
        history.push({ pathname: '/call', state: { selectedBroadCast: joinedRoom } });
      }
      return;
    }

    if (user.gold_coin === '0') {
      setIsJoinRoom(false);
      setIsNoGoldPiece(true);
    } else {
      setIsJoinRoom(false);
      document.body.classList.remove('modal-open');
      if (joinIntent === 'chat') {
        sendChatJoinRequest(joinedRoom);
      } else {
        history.push({ pathname: '/call', state: { selectedBroadCast: joinedRoom } });
      }
    }
  }, [history, joinIntent, joinedRoom, sendChatJoinRequest, user.gold_coin, user?.pk]);

  const handleRechargeAccount = useCallback(() => {
    setIsNoGoldPiece(false);
    setIsNoFees(false);
    trackNavigation('/currency', 'insufficient_gold_modal');
    document.body.classList.remove('modal-open');
    history.push('/currency');
  }, [history]);

  const handleBrowseRooms = useCallback(() => {
    setIsNoGoldPiece(false);
    trackNavigation('/conversations-nearby', 'insufficient_gold_modal');
    document.body.classList.remove('modal-open');
    history.push('/conversations-nearby');
  }, [history]);

  const handleStartRoom = useCallback(() => {
    setIsJoinRoom(false);
    setCreateRoomPopup(true);
  }, []);

  const handleSuperHost = useCallback(() => {
    setIsNoFees(false);
    document.body.classList.remove('modal-open');
    if (joinIntent === 'chat') {
      sendChatJoinRequest(joinedRoom);
    } else {
      history.push({ pathname: '/call', state: { selectedBroadCast: joinedRoom } });
    }
  }, [history, joinIntent, joinedRoom, sendChatJoinRequest]);

  const handleExpandModalOpenChat = useCallback(() => {
    history.push({ pathname: '/chat-room', state: { selected: expandedRoom, preserveSelection: true } });
    closeDialog();
  }, [closeDialog, expandedRoom, history]);

  const handleExpandModalJoin = useCallback(() => {
    const room = expandedRoom;
    const participants = getParticipantsArray(room?.participants);

    if (joined) {
      handleExpandModalOpenChat();
      return;
    }

    setJoinIntent('chat');
    setJoinRoom(room);
    setIsExpand(false);
    document.body.classList.add('modal-open');

    if (room.host.is_super_host) {
      setIsNoFees(true);
    } else if (participants.length >= 3) {
      setIsJoinRoom(true);
    } else {
      document.body.classList.remove('modal-open');
      sendChatJoinRequest(room);
    }
  }, [closeDialog, expandedRoom, handleExpandModalOpenChat, joined, sendChatJoinRequest]);

  const handleGroupChatClick = useCallback(() => {
    trackNavigation('/chat-room', 'group_chat_fab');
    history.push('/chat-room');
  }, [history]);

  const handleSearch = useCallback((topic: string) => {
    setSearchTopic(topic);
    if (topic !== '') {
      nearbyRooms({ topic });
    } else {
      nearbyRooms({});
    }
  }, [nearbyRooms]);

  const getLocation = useCallback(() => {
    if (locationFetched.current) return;
    if (!navigator.geolocation) {
      alert('No geolocation support');
      return;
    }
    locationFetched.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          longitude: pos.coords.longitude.toString(),
          latitude: pos.coords.latitude.toString(),
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setModalToggle(true);
      },
      { timeout: 10000, enableHighAccuracy: false, maximumAge: 600000 }
    );
  }, []);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Auth guard
  useEffect(() => {
    if (!user && token === '') history.push('/login');
  }, []);

  // Track modal interactions
  const prevModals = useRef({
    isExpand: false,
    isJoinRoom: false,
    isNoGoldPiece: false,
    isNoFees: false,
    viewProfile: false,
    modalToggle: false,
  });

  useEffect(() => {
    const current = { isExpand, isJoinRoom, isNoGoldPiece, isNoFees, viewProfile, modalToggle };
    
    if (current.isExpand !== prevModals.current.isExpand) {
      trackModalInteraction("room_details_expand", current.isExpand ? "opened" : "closed");
    }
    if (current.isJoinRoom !== prevModals.current.isJoinRoom) {
      trackModalInteraction("gold_piece_join_confirm", current.isJoinRoom ? "opened" : "closed");
    }
    if (current.isNoGoldPiece !== prevModals.current.isNoGoldPiece) {
      trackModalInteraction("insufficient_gold_error", current.isNoGoldPiece ? "opened" : "closed");
    }
    if (current.isNoFees !== prevModals.current.isNoFees) {
      trackModalInteraction("super_host_no_fees_info", current.isNoFees ? "opened" : "closed");
    }
    if (current.viewProfile !== prevModals.current.viewProfile) {
      trackModalInteraction("view_public_profile", current.viewProfile ? "opened" : "closed");
    }
    if (current.modalToggle !== prevModals.current.modalToggle) {
      trackModalInteraction("location_permission_required", current.modalToggle ? "opened" : "closed");
    }

    prevModals.current = current;
  }, [isExpand, isJoinRoom, isNoGoldPiece, isNoFees, viewProfile, modalToggle]);

  // Track search interactions debounced
  useEffect(() => {
    if (searchTopic.trim() !== '') {
      trackSearch("rooms", searchTopic.length, conversationNearby.length > 0);
    }
  }, [searchTopic, conversationNearby.length]);

  // Geolocation — run once on mount
  useEffect(() => { getLocation(); }, [getLocation]);

  // Create location + fetch rooms when coords arrive
  useEffect(() => {
    if (location.latitude && location.longitude) {
      createLocation(location);
      nearbyRooms({});
    }
  }, [location.latitude, location.longitude]);

  // Public profile
  useEffect(() => {
    getPublicProfile({
      pk: profile.pk,
      ...(profileRoomPk ? { room_pk: profileRoomPk } : {}),
    });
  }, [profile.pk, profileRoomPk]);
  useEffect(() => { setPublicProfile(userPublicProfile); }, [userPublicProfile]);

  // Set joined flag when expanded room changes
  useEffect(() => {
    if (expandedRoom.host?.pk === user.pk) {
      setJoined(true);
      return;
    }
    const participants = getParticipantsArray(expandedRoom?.participants);
    setJoined(participants.some((p: any) => p.pk === user.pk));
  }, [expandedRoom.pk, expandedRoom?.participants, user.pk]);

  // Sort + merge rooms when API data updates
  useEffect(() => {
    if (!allNearByRooms?.length) return;

    const withLoc: any[]    = [];
    const withoutLoc: any[] = [];

    allNearByRooms.forEach((doc: any) => {
      if (doc.host?.latitude && doc.host?.longitude) {
        withLoc.push({
          ...doc,
          distance: getDistance(user?.latitude, user?.longitude, doc.host.latitude, doc.host.longitude),
        });
      } else {
        withoutLoc.push({ ...doc, distance: normalizeDistance(null) });
      }
    });

    const sortFn = (a: any, b: any) => {
      const aVideo = a.status === 'VIDEO', bVideo = b.status === 'VIDEO';
      if (aVideo !== bVideo) return aVideo ? -1 : 1;
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.created_at > b.created_at ? -1 : 0;
    };

    withLoc.sort(sortFn);
    withoutLoc.sort(sortFn);

    setBaseRooms([...withLoc, ...withoutLoc]);
  }, [allNearByRooms]);

  // ── Modals ────────────────────────────────────────────────────────────────
  const isAnyModalOpen = isExpand || isJoinRoom || isNoGoldPiece || isNoFees || viewProfile || modalToggle;

  const handleExpandedRoom = () => {
    const expandedParticipants = getParticipantsArray(expandedRoom?.participants);

    return (
      <>
        <header className={Styles.expandModalHeader}>
          <h5 className={`m-0 ${Styles.expandModalHeaderTitle}`}>
            TOPIC:
            <span className={Styles.topicButton}>{expandedRoom.topic}</span>
          </h5>
          <div className={Styles.expandModalMeta}>
            <div className="d-flex align-items-center column-gap-10">
              <img src={locationInModalIcon} alt="location" />
              <span className={Styles.expandModalMetaText}>
                {getDistance(user.latitude, user.longitude, expandedRoom.host.latitude, expandedRoom.host.longitude)} KM
              </span>
            </div>
            <div className="d-flex align-items-center column-gap-10">
              <img src={peopleGroupInModalIcon} alt="people in room" />
              <span className={Styles.expandModalMetaText}>
                {`${expandedParticipants.length + 1} people are in this room`}
              </span>
            </div>
          </div>
        </header>
        <div className={Styles.expandModalBody}>
          <div className={Styles.hostWrapper}>
            <div
              className={Styles.hostInner}
              onClick={() => openProfileForRoom(expandedRoom.host, expandedRoom.pk)}
              onKeyPress={() => openProfileForRoom(expandedRoom.host, expandedRoom.pk)}
              role="button"
              tabIndex={0}
            >
              <ProfileAvatar profile={expandedRoom.host.profile} alt="host" className={Styles.hostImage} />
              {expandedRoom.host.is_super_host && <img src={superHostKey} alt="super host" className={Styles.superKeyImage} />}
            </div>
            <div>
              <h5 className={Styles.hostName}>{expandedRoom.host.name}</h5>
              <span className={Styles.hostRole}>
                {expandedRoom.host.is_super_host ? 'SUPER HOST' : 'Host'}
              </span>
            </div>
          </div>
          <div className={Styles.expandRoomListing}>
            {expandedParticipants.map((i: any, key: number) => (
              <div
                className={`text-center ${Styles.expandRoomDiv}`}
                key={i.pk || key}
                onClick={() => openProfileForRoom(i, expandedRoom.pk)}
                onKeyPress={() => openProfileForRoom(i, expandedRoom.pk)}
                role="button"
                tabIndex={0}
              >
                <ProfileAvatar profile={i.profile} alt="participant" />
                <span className={Styles.expandParticipantName}>{i.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={Styles.goldPieceActions}>
          <button type="button" className={Styles.goldPieceActionBtn} onClick={closeDialog}>
            Close
            <img src={closeModalIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
          </button>
          {expandedRoom.host?.pk === user.pk ? (
            <button type="button" className={Styles.goldPieceActionBtn} onClick={handleExpandModalOpenChat}>
              Open Chat
              <img src={chatOutlineIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
            </button>
          ) : (
            <button type="button" className={Styles.goldPieceActionBtn} onClick={handleExpandModalJoin}>
              {joined ? 'Open Chat' : 'Join Room'}
              <img
                src={joined ? chatOutlineIcon : joinGroupIcon}
                alt=""
                className={Styles.goldPieceActionBtnIcon}
              />
            </button>
          )}
        </div>
      </>
    );
  };

  const goldPieceModal = () => (
    <div className={Styles.goldPieceWrapper}>
      <img src={logoImage} alt="Moderate Politics" className={Styles.goldPieceLogo} />
      <p className={Styles.goldPieceModalText}>Spend 1 Gold Piece and join the room?</p>
      <div className={Styles.goldPieceActions}>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={joinGoldPiece}>
          Join Room
          <img src={joinGroupIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={handleStartRoom}>
          Start Your Own Room
          <img src={startYourOwnRoomIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
      </div>
    </div>
  );

  const noGoldPiece = () => (
    <div className={Styles.noGoldPieceWrapper}>
      <img src={warningCircleIcon} alt="" className={Styles.noGoldPieceIcon} />
      <div className={Styles.noGoldPieceText}>Uh oh! It looks like you don't have any gold pieces.</div>
      <div className={Styles.goldPieceActions}>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={handleRechargeAccount}>
          Recharge Account
          <img src={rechargeAccountIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={handleBrowseRooms}>
          Browse Rooms
          <img src={browseRoomsIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
      </div>
    </div>
  );

  const superHostModal = () => (
    <div className={Styles.superHostWrapper}>
      <img src={superHostJoinIcon} alt="" className={Styles.superHostIcon} />
      <div className={Styles.superHostText}>
        This is a Super Host room and
        <br />
        has no fees.
        <br />
        Thank your host!
      </div>
      <div className={Styles.goldPieceActions}>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={closeDialog}>
          Close
          <img src={closeModalIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={handleSuperHost}>
          Join Room
          <img src={joinGroupIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
      </div>
    </div>
  );

  const publicProfileModal = () => (
    <>
      <section className={Styles.publicProfileScroll}>
        <div className={`${ProfileStyles.profileSection} ${Styles.modalProfileSection}`}>
          <div className={ProfileStyles.profileBox}>
            <div className={ProfileStyles.profileBoxInner}>
              <div className={ProfileStyles.profileImageWrapper}>
                <ProfileAvatar profile={profile.profile} loading="lazy" className={ProfileStyles.profileImage} alt="profile" />
                {profile.is_super_host && (
                  <img src={superHostKey} loading="lazy" alt="super host" className={ProfileStyles.superHostKey} />
                )}
              </div>
              <div className={ProfileStyles.profileMeta}>
                <h3>{profile.name}</h3>
                <p>ID NUMBER: {profile.pk}</p>
              </div>
            </div>
            <div className="position-relative">
              {user.pk !== profile.pk && (
                <button
                  type="button"
                  className={ProfileStyles.followBtn}
                  onClick={handleFollow}
                  onKeyUp={handleFollow}
                  onMouseEnter={() => setShowToolTip(true)}
                  onMouseLeave={() => setShowToolTip(false)}
                >
                  {publicProfile?.is_profile_follow ? 'Unfollow' : 'Follow'}
                </button>
              )}
              {showToolTip && (
                <div className={`${ProfileStyles.followToolTip} ${Styles.leftZero}`}>
                  <p className={`mb-0 ${ProfileStyles.toolTip}`}>
                    Follow to receive notifications when this host starts a room.
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className={ProfileStyles.cardWrapper}>
            {profileCardDetails.map((item, key) => (
              <CardItem
                title={item.title}
                description={item.description}
                total={item.total}
                breakdown={item.breakdown}
                key={key}
              />
            ))}
          </div>
        </div>
        <hr className={ProfileStyles.sectionDivider} />
        <div className={`${ProfileStyles.openCardWrapper} ${Styles.modalOpenCardWrapper}`}>
          {selectedProfileRoom?.topic && (
            <div className={ProfileStyles.openRoomCard}>
                <div className={ProfileStyles.openCardInner}>
                  <div>
                    <div className="d-flex justify-content-start align-items-center">
                      <h5 className={ProfileStyles.topicLabel}>TOPIC:</h5>
                      <div className={ProfileStyles.topicBtn}>{selectedProfileRoom.topic}</div>
                    </div>
                    <div className={`d-flex justify-content-start align-items-center column-gap-20 mt-4 ${ProfileStyles.distanceWrapper}`}>
                      <div>
                        <img src={distanceIcon} alt="distance" />
                        <span>
                          {selectedProfileRoom.distance != null
                            ? `${Number(selectedProfileRoom.distance).toFixed(2)} KM`
                            : publicProfile?.distance_in_km != null
                              ? `${publicProfile.distance_in_km.toFixed(2)} KM`
                              : '—'}
                        </span>
                      </div>
                      <div>
                        <img src={userIcon} alt="people" />
                        <span>
                          {getParticipantsCount(selectedProfileRoom.participants)} people are in this room
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          )}
        </div>
      </section>
      <div className={Styles.goldPieceActions}>
        <button type="button" className={Styles.goldPieceActionBtn} onClick={closeDialog}>
          Cancel
          <img src={closeModalIcon} alt="" className={Styles.goldPieceActionBtnIcon} />
        </button>
      </div>
    </>
  );

  const roomCardsSection = (
    <div className="custom-container">
      {searchTopic !== '' && (
        <div className="text-center">
          <h6 className={Styles.searchResultsTopic}>
            Search results for &ldquo;{searchTopic}&rdquo;
          </h6>
        </div>
      )}

      {loader && (
        <div className={isMobileLayout ? Styles.nearbyCardsStack : Styles.skeletonRow}>
          {Array.from({ length: isMobileLayout ? 3 : SKELETON_COUNT }).map((_, i) => (
            isMobileLayout ? <MobileSkeletonCard key={i} /> : <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {!loader && conversationNearby.length === 0 && (
        <div className={Styles.emptyState}>
          <div className={Styles.emptyIcon}>
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </div>
          <h4 className={Styles.emptyTitle}>
            {searchTopic ? `No rooms found for "${searchTopic}"` : 'No conversations nearby'}
          </h4>
          <p className={Styles.emptySubtitle}>
            {searchTopic
              ? 'Try a different search term or browse all rooms.'
              : 'Be the first to start a conversation in your area!'}
          </p>
          {searchTopic && (
            <button
              className={Styles.clearSearchBtn}
              onClick={() => handleSearch('')}
              type="button"
            >
              Clear search
            </button>
          )}
        </div>
      )}

      {!loader && conversationNearby.length > 0 && (
        isMobileLayout ? (
          <div className={Styles.nearbyCardsStack}>
            {conversationNearby.map((item: any) => (
              <NearbyItem
                key={item.pk}
                item={item}
                conversations={conversationNearby}
                handleExpand={() => {
                  const firebaseRoom = firebaseRooms.find((r: any) => r.pk === item.pk);
                  const room = firebaseRoom || item;
                  if (getParticipantsArray(room.participants).length === 0) return;
                  setIsExpand(true);
                  setExpandedRoom(room);
                }}
                joinRoom={setJoinRoom}
                roomWithFees={setIsJoinRoom}
                roomWithNoFees={setIsNoFees}
                setJoinIntent={setJoinIntent}
                profile={openProfileForRoom}
              />
            ))}
          </div>
        ) : (
          <SliderWrapper sliderSetting={broadCastNearby} slideToShow={6} goto={6}>
            {conversationNearby.map((item: any) => (
              <NearbyItem
                key={item.pk}
                item={item}
                conversations={conversationNearby}
                handleExpand={() => {
                  const firebaseRoom = firebaseRooms.find((r: any) => r.pk === item.pk);
                  const room = firebaseRoom || item;
                  if (getParticipantsArray(room.participants).length === 0) return;
                  setIsExpand(true);
                  setExpandedRoom(room);
                }}
                joinRoom={setJoinRoom}
                roomWithFees={setIsJoinRoom}
                roomWithNoFees={setIsNoFees}
                setJoinIntent={setJoinIntent}
                profile={openProfileForRoom}
              />
            ))}
          </SliderWrapper>
        )
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <div className={`${Styles.wrapper} appPageScroll ${isAnyModalOpen ? Styles.modalOpen : ''}`}>
        <Header showPopup={createRoomPopup} callStatus={setCallStatus} />

        <div className={Styles.body}>
          <button
            type="button"
            className={Styles.groupChat}
            aria-label="Group chat"
            onClick={handleGroupChatClick}
          >
            <span className={Styles.fabBroadcast} aria-hidden="true">
              <span className={Styles.fabBroadcastPulse} />
              <span className={`${Styles.fabBroadcastPulse} ${Styles.fabBroadcastPulseDelay}`} />
            </span>
            <img src={groupChatIcon} alt="Group chat" />
          </button>

          {/* Search bar (desktop) / mobile section (title + sticky search + cards) */}
          {isMobileLayout ? (
            <div className={Styles.mobileSection}>
              <div className={Styles.mobileTitleBar}>
                <div className="custom-container">
                  <p className={Styles.conversationsNearbyTitle}>Conversations Nearby</p>
                </div>
              </div>
              <div className={Styles.mobileScrollSection}>
                <CompactMobileSearch
                  value={searchTopic}
                  onChange={handleSearch}
                  onBlur={() => handleMobileSearchBlur(searchTopic)}
                  placeholder="Search for topics near you"
                  searchIcon={searchIcon}
                  inputClassName={Styles.nearbyMobileSearchField}
                  wrapClassName={Styles.nearbyCompactSearchWrap}
                  iconButtonClassName={showCompactSearch ? '' : Styles.nearbySearchIconBtn}
                  iconImgClassName={Styles.nearbySearchIconImg}
                  showCompactSearch={showCompactSearch}
                  isSearchExpanded={isSearchExpanded}
                  searchInputRef={searchInputRef}
                  onOpenMobileSearch={openMobileSearch}
                  openSearchAriaLabel="Open search"
                  focusSearchAriaLabel="Search for topics near you"
                />
                <div className={Styles.nearbyItemWrapper}>
                  {roomCardsSection}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className={Styles.converNearByBg}>
                <div className="custom-container">
                  <div className={Styles.searchbarWrapper}>
                    <p className={Styles.conversationsNearbyTitle}>Conversations Nearby</p>
                    <div className={`${Styles.searchBox} form-group mb-0`}>
                      <InputField
                        placeholder="Search for topics near you"
                        cssClass={Styles.searchField}
                        type="txt"
                        icon={searchIcon}
                        handleChange={handleSearch}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={Styles.nearbyItemWrapper}>
                {roomCardsSection}
              </div>
            </>
          )}
        </div>

        <Footer />
      </div>

      {/* Modals */}
      {isExpand && (
        <CustomModal
          closeDialog={closeDialog}
          wrapperClass={Styles.expandModalShell}
        >
          {handleExpandedRoom()}
        </CustomModal>
      )}
      {isJoinRoom && (
        <CustomModal
          wrapperClass={Styles.wrapperGoldModal}
          title="Joining a room with more than 3 people will cost 1 Gold Piece."
          modalTitleClass={Styles.goldPieceModalTitle}
          modalHeaderClass={Styles.goldPieceModalHeader}
          hideHeaderBorder
          closeDialog={closeDialog}
        >
          {goldPieceModal()}
        </CustomModal>
      )}
      {isNoGoldPiece && (
        <CustomModal wrapperClass={Styles.wrapperNoGoldPiece} hideHeaderBorder closeDialog={closeDialog}>
          {noGoldPiece()}
        </CustomModal>
      )}
      {isNoFees && (
        <CustomModal wrapperClass={Styles.wrapperNoPiece} hideHeaderBorder closeDialog={closeDialog}>
          {superHostModal()}
        </CustomModal>
      )}
      {viewProfile && (
        <CustomModal
          wrapperClass={Styles.publicProfileWrapper}
          heightClass={Styles.publicProfileBody}
          hideHeader
          closeDialog={closeDialog}
        >
          {publicProfileModal()}
        </CustomModal>
      )}
      {modalToggle && (
        <CustomModal
          modalTitle="Location Permission Required"
          wrapperClass={Styles.locationModalWrapper}
          heightClass={Styles.locationModalBody}
          closeDialog={closeDialog}
          modalTitleClass={Styles.modalTitleClass}
          closeModal={closeModal}
          close
        >
          <div className={Styles.locationGuideModal}>
            {[
              { n: 1, title: 'Click the Information Icon', body: 'Look for the ⓘ icon to the left of the address bar (next to the refresh button)' },
              { n: 2, title: 'Find Location Permission', body: 'In the dropdown menu, look for "Location" or "Location sharing"' },
              { n: 3, title: 'Enable Location', body: 'Change the setting from "Block" to "Allow" for this site' },
              { n: 4, title: 'Refresh the Page', body: 'Click the refresh button or reload the page to see nearby rooms' },
            ].map(step => (
              <div className={Styles.guideStep} key={step.n}>
                <div className={Styles.stepNumber}>{step.n}</div>
                <div className={Styles.stepContent}>
                  <h4>{step.title}</h4>
                  <p>{step.body}</p>
                </div>
              </div>
            ))}
            <div className={Styles.helpLink}>
              <a href="https://support.google.com/chrome/answer/142065?hl=en&co=GENIE.Platform%3DDesktop" target="_blank" rel="noopener noreferrer">
                For more information, click here
              </a>
            </div>
            <div className={Styles.guideActions}>
              <Button label="Refresh Page" clickHandler={() => window.location.reload()} cssClass={Styles.refreshButton} />
              <Button label="Got It" clickHandler={closeModal} cssClass={Styles.gotItButton} />
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default ConversationsNearby;
